import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { TemplateQuery } from './types/template-query.type';
import type { Template } from './types/template.type';

/**
 * Retrieves a template by either its ID or name using the provided Ky HTTP client instance.
 *
 * @param instance - The KyInstance used to make the HTTP request.
 * @param query - An object containing either the `id` or `name` of the template to retrieve.
 * @returns A promise that resolves to an OperationResultWithData containing the requested Template.
 * @throws Will throw an error if neither `id` nor `name` is provided in the query, or if the request fails.
 */
export const getTemplate = async (instance: KyInstance, query: TemplateQuery) => {
  try {
    const { id, name } = query;

    if (isNotDefined(id) && isNotDefined(name)) {
      throw new Error('Either id or name must be provided to get a template.');
    }

    return await instance
      .get<OperationResultWithData<Template>>(formatString(Uri.template.getTemplatesBy, id?.toString() ?? name ?? ''))
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
